import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  Joyride,
  ACTIONS,
  EVENTS,
  STATUS,
  type EventData,
  type Controls,
} from 'react-joyride'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useAuth } from '@/hooks/useAuth'
import { useInfiniteMembers } from '@/hooks/useInfiniteMembers'
import { useAccessControlledTasks } from '@/hooks/useAccessControlledTasks'
import { useAccessControlledCareReasons } from '@/hooks/useAccessControlledCareReasons'
import { buildTourSteps, tourSteps as fallbackSteps } from './tourSteps'
import TourTooltip from './TourTooltip'
import { DemoTourContext } from './useDemoTour'

const TOUR_COMPLETED_KEY = 'flock-demo-tour-completed'

function waitForElement(
  selector: string,
  timeout = 3000,
): Promise<Element | null> {
  return new Promise((resolve) => {
    const existing = document.querySelector(selector)
    if (existing) {
      resolve(existing)
      return
    }

    const start = Date.now()
    const interval = setInterval(() => {
      const el = document.querySelector(selector)
      if (el) {
        clearInterval(interval)
        resolve(el)
      } else if (Date.now() - start > timeout) {
        clearInterval(interval)
        resolve(null)
      }
    }, 100)
  })
}

export default function DemoTourProvider({
  children,
}: {
  children: ReactNode
}) {
  const { isDemo } = useAuth()
  const navigate = useNavigate()
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname
  const isDesktop = useMediaQuery('(min-width:900px)')

  const [run, setRun] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const navigatingRef = useRef(false)

  // Fetch first IDs from demo data for detail page steps
  const { data: infiniteMembers } = useInfiniteMembers()
  const { data: tasks } = useAccessControlledTasks()
  const { data: careReasons } = useAccessControlledCareReasons()

  const demoIds = useMemo(() => {
    if (!isDemo) return {}
    return {
      memberId: infiniteMembers?.pages[0]?.members[0]?.id,
      taskId: tasks?.[0]?.id,
      reasonId: careReasons?.[0]?.id,
    }
  }, [isDemo, infiniteMembers, tasks, careReasons])

  const tourSteps = useMemo(
    () => (isDemo ? buildTourSteps(demoIds) : fallbackSteps),
    [isDemo, demoIds],
  )

  // Keep a ref so the handleEvent callback always sees the latest steps
  const stepsRef = useRef(tourSteps)
  useEffect(() => {
    stepsRef.current = tourSteps
  }, [tourSteps])

  // Auto-start tour on first dashboard visit for demo users (desktop only)
  useEffect(() => {
    if (!isDemo || !isDesktop) return
    if (localStorage.getItem(TOUR_COMPLETED_KEY)) return
    if (currentPath !== '/dashboard') return

    const timer = setTimeout(() => {
      waitForElement('[data-tour="sidebar-nav"]', 5000).then((el) => {
        if (el && !localStorage.getItem(TOUR_COMPLETED_KEY)) {
          setStepIndex(0)
          setRun(true)
        }
      })
    }, 800)

    return () => clearTimeout(timer)
  }, [isDemo, isDesktop, currentPath])

  const startTour = useCallback(() => {
    localStorage.removeItem(TOUR_COMPLETED_KEY)
    navigate({ to: '/dashboard' }).then(() => {
      waitForElement('[data-tour="sidebar-nav"]', 5000).then(() => {
        setStepIndex(0)
        setRun(true)
      })
    })
  }, [navigate])

  const stopTour = useCallback(() => {
    setRun(false)
    localStorage.setItem(TOUR_COMPLETED_KEY, 'true')
  }, [])

  const handleEvent = useCallback(
    async (data: EventData, _controls: Controls) => {
      const { action, index, type, status } = data
      const steps = stepsRef.current

      // Tour finished or skipped
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        setRun(false)
        localStorage.setItem(TOUR_COMPLETED_KEY, 'true')
        return
      }

      // Close button
      if (action === ACTIONS.CLOSE) {
        setRun(false)
        localStorage.setItem(TOUR_COMPLETED_KEY, 'true')
        return
      }

      if (type === EVENTS.STEP_AFTER) {
        const nextIndex = action === ACTIONS.PREV ? index - 1 : index + 1
        if (nextIndex < 0) return
        if (nextIndex >= steps.length) {
          setRun(false)
          localStorage.setItem(TOUR_COMPLETED_KEY, 'true')
          return
        }

        const nextStep = steps[nextIndex]
        const currentStep = steps[index]

        // If the next step is on a different page, navigate first
        if (nextStep.page !== currentStep.page) {
          navigatingRef.current = true
          setRun(false)

          await navigate({ to: nextStep.page })
          await waitForElement(nextStep.target as string, 5000)

          navigatingRef.current = false
          setStepIndex(nextIndex)
          setRun(true)
        } else {
          setStepIndex(nextIndex)
        }
      }
    },
    [navigate],
  )

  const contextValue = useMemo(
    () => ({
      isActive: run,
      currentStep: stepIndex,
      totalSteps: tourSteps.length,
      startTour,
      stopTour,
    }),
    [run, stepIndex, tourSteps.length, startTour, stopTour],
  )

  return (
    <DemoTourContext.Provider value={contextValue}>
      {children}
      {isDemo && isDesktop && (
        <Joyride
          steps={tourSteps}
          run={run}
          stepIndex={stepIndex}
          continuous
          onEvent={handleEvent}
          tooltipComponent={TourTooltip}
          scrollToFirstStep
          options={{
            overlayColor: 'rgba(0, 0, 0, 0.45)',
            overlayClickAction: false,
            zIndex: 1400,
            spotlightRadius: 12,
            buttons: ['back', 'close', 'primary', 'skip'],
          }}
        />
      )}
    </DemoTourContext.Provider>
  )
}
